import { Controller } from "stimulus";

// Collapsed customer-donations cart-drawer widget ("Option C3 — PayPal footer").
// Server-rendered (.vcd-*) and driven here. Runs inside the widgets iframe, so it
// never writes the cart directly — it postMessages the parent (chooseCustomerCause /
// changeCustomerCause / removeCustomerCause), matching the expanded widget's contract.
// The checkbox adds/removes the donation; the amount & charity pills open in-flow
// dropdowns (the iframe is resized via sendCurrentHeight so they aren't clipped).
export default class extends Controller {
  static targets = [
    "widget",
    "main",
    "causeSelection",
    "checkbox",
    "amountButton",
    "amountLabel",
    "amountMenu",
    "amountOption",
    "customOption",
    "customDisplay",
    "customInput",
    "chosenCause",
    "causeMenu",
    "causeList",
    "footerFull",
    "footerToggle"
  ];

  initialize() {
    // Close any open dropdown when clicking outside both the pill and its (in-flow) menu panel —
    // so clicking inside the menu (e.g. the custom-amount input) keeps it open. Set this up first
    // so connect()/disconnect() can always (de)register it, even in the no-causes empty state.
    this.onDocClick = (e) => {
      if (!e.target.closest(".vcd-dd") && !e.target.closest(".vcd-menu")) this._closeDropdowns();
    };

    // The no-causes empty state renders without the cause targets. Bail out of the cause-dependent
    // setup so initialization doesn't throw — otherwise connect()/displayWidget never run and the
    // parent keeps the iframe hidden instead of showing the no-causes message.
    if (!this.hasChosenCauseTarget || !this.hasCauseSelectionTarget) return;

    this.saveCauseMetaData(this.chosenCauseTarget.dataset["metadata"]);

    this.widgetSource = this.element.dataset["widgetSource"] || "cart_drawer";
    this.roundUp = this.element.dataset["roundUp"] == "1";
    this.fixedWholeAmt = this.element.dataset["fixedWholeAmt"] == "1";
    this.variantType = this.element.dataset["variantType"];
    this.customVariantType = this.element.dataset["customVariantType"];
    this.currencyRate = parseFloat(this.element.dataset["currencyRate"]);
    this.productValue = parseFloat(this.element.dataset["productValue"]);
    this.wholeProductValue = parseFloat(this.element.dataset["wholeProductValue"]);
    this.defaultVariantId = this.element.dataset["defaultVariantId"];
    this.hasBundleFunction = this.element.dataset["bundleFunction"] == "1";

    this.causeIsSelected = this.causeSelectionTarget.dataset["chosenCause"] == "true";
    this.customMode = this.hasCustomOptionTarget && this.customOptionTarget.classList.contains("vcd-selected");

    if (this.causeIsSelected) {
      this.previousVariantId = this.element.dataset["chosenCauseVariantId"] || null;
      this.selectedQuantity = this.causeSelectionTarget.dataset["quantity"];
    } else {
      this.previousVariantId = null;
      const sel = this.amountOptionTargets.find((o) => o.classList.contains("vcd-selected")) || this.amountOptionTargets[0];
      this.selectedQuantity = sel ? sel.dataset["quantity"] : null;
    }

    this.variantIds = [];
    this.centsVariantIds = [];
    if (this.causeListTargets.length > 0) {
      this.causeListTargets.forEach((cause) => {
        this.variantIds.push(cause.dataset["variantId"]);
        this.centsVariantIds.push(cause.dataset["centsVariantId"]);
      });
    } else {
      this.variantIds.push(this.chosenCauseTarget.dataset["variantId"]);
      this.centsVariantIds.push(this.chosenCauseTarget.dataset["centsVariantId"]);
    }
  }

  connect() {
    document.addEventListener("click", this.onDocClick);
    parent.postMessage(JSON.stringify({ checkChosenCauseOnLoad: true }), this.element.dataset.location);
    this.sendCurrentHeight();
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick);
  }

  /* ---------- checkbox: add / remove ---------- */

  toggleEnabled(e) {
    if (e.target.checked) {
      if (!this.selectedQuantity) return; // nothing valid to add
      this._applyToCart(this.customMode);
    } else {
      if (this.previousVariantId) {
        parent.postMessage(JSON.stringify({
          removeCustomerCause: true,
          variantId: this.previousVariantId,
          productId: this.mainTarget.dataset["productId"],
          widgetSource: this.widgetSource
        }), this.element.dataset.location);
      }
      this.causeIsSelected = false;
      this.previousVariantId = null;
    }
  }

  /* ---------- amount dropdown ---------- */

  toggleAmountDropdown(e) {
    const open = this.amountMenuTarget.classList.toggle("d-none") === false;
    e.currentTarget.setAttribute("aria-expanded", open.toString());
    if (this.hasCauseMenuTarget) {
      this.causeMenuTarget.classList.add("d-none");
      this.chosenCauseTarget.setAttribute("aria-expanded", "false");
    }
    this.sendCurrentHeight();
  }

  selectAmount(e) {
    const clicked = e.currentTarget;
    const quantity = clicked.dataset["quantity"];
    if (this.causeIsSelected && !this.customMode && this.selectedQuantity &&
        parseFloat(this.selectedQuantity) == parseFloat(quantity)) {
      this._closeDropdowns();
      return;
    }
    this.customMode = false;
    this.selectedQuantity = quantity;
    this.amountLabelTarget.textContent = (clicked.querySelector("span")?.textContent || clicked.textContent).trim();
    this._markSelected(this.amountOptionTargets, clicked);
    if (this.hasCustomOptionTarget) this.customOptionTarget.classList.remove("vcd-selected");
    this._closeDropdowns();
    if (this.causeIsSelected) this._applyToCart(false);
  }

  showCustom(e) {
    if (this.hasCustomOptionTarget) this.customOptionTarget.classList.add("d-none");
    this.customDisplayTarget.classList.remove("d-none");
    this.customInputTarget?.focus();
    this.sendCurrentHeight();
  }

  addCustomAmount(e) {
    const raw = this.customInputTarget?.value;
    if (!raw || parseFloat(raw) <= 0) return;
    const entered = parseInt(raw);
    this.customMode = true;
    this.selectedQuantity = entered / (this.customVariantType == "whole" ? this.wholeProductValue : this.productValue);
    this.amountLabelTarget.textContent = this._formatCurrency(entered);
    this._markSelected(this.amountOptionTargets, null);
    if (this.hasCustomOptionTarget) this.customOptionTarget.classList.add("vcd-selected");
    this._resetCustomInput();
    this._closeDropdowns();
    if (this.causeIsSelected) this._applyToCart(true);
  }

  customKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      this.addCustomAmount(e);
    } else if (e.key === "Escape") {
      this._resetCustomInput();
      this._closeDropdowns();
    }
  }

  /* ---------- charity dropdown ---------- */

  toggleCauseDropdown(e) {
    if (!this.hasCauseMenuTarget) return;
    const open = this.causeMenuTarget.classList.toggle("d-none") === false;
    e.currentTarget.setAttribute("aria-expanded", open.toString());
    this.amountMenuTarget.classList.add("d-none");
    this.amountButtonTarget.setAttribute("aria-expanded", "false");
    this.sendCurrentHeight();
  }

  setSelectedCause(e) {
    const clicked = e.currentTarget;
    if (this.previousVariantId && clicked.dataset["variantId"] &&
        clicked.dataset["variantId"].toString() == this.previousVariantId.toString()) {
      this._closeDropdowns();
      return;
    }
    this.chosenCauseTarget.dataset["variantId"] = clicked.dataset["variantId"];
    this.chosenCauseTarget.dataset["centsVariantId"] = clicked.dataset["centsVariantId"];
    const org = (clicked.querySelector(".cause-organization")?.textContent || clicked.textContent).trim();
    const label = this.chosenCauseTarget.querySelector(".vcd-pill-label") || this.chosenCauseTarget;
    label.textContent = org;
    this.chosenCauseTarget.setAttribute("title", org);
    this._markSelected(this.causeListTargets, clicked);
    this.saveCauseMetaData(clicked.dataset["metadata"]);
    this._updatePpgfVisibility(clicked.dataset["paypal"] === "true");
    this._closeDropdowns();
    if (this.causeIsSelected) this._applyToCart(this.customMode);
  }

  /* ---------- footer disclosure ---------- */

  toggleDisclosure(e) {
    if (!this.hasFooterFullTarget) return;
    const expanded = this.footerFullTarget.classList.toggle("d-none") === false;
    e.currentTarget.setAttribute("aria-expanded", expanded.toString());
    this.sendCurrentHeight();
  }

  /* ---------- cart sync (postMessage to parent) ---------- */

  _applyToCart(isCustom) {
    const variantId = this._resolveVariantId(isCustom);
    const base = {
      variantId,
      productId: this.mainTarget.dataset["productId"],
      quantity: this.selectedQuantity,
      amount: this.calculateAmount(variantId, this.selectedQuantity),
      widgetSource: this.widgetSource,
      defaultVariantId: this.defaultVariantId,
      hasBundleFunction: this.hasBundleFunction
    };
    let data;
    if (this.causeIsSelected) {
      data = { changeCustomerCause: true, previousVariantId: this.previousVariantId, ...base };
    } else {
      data = { chooseCustomerCause: true, ...base };
      this.causeIsSelected = true;
      this.trackController?.trackEvent(this.checkboxTarget, "added donation to cart", { ...this.causeMetaData, amount: parseInt(this.selectedQuantity) });
    }
    this.previousVariantId = variantId;
    parent.postMessage(JSON.stringify(data), this.element.dataset.location);
  }

  _resolveVariantId(isCustom) {
    // A custom amount's quantity is computed against customVariantType's product value (see
    // addCustomAmount), so the variant MUST match customVariantType — even when a donation is
    // already in the cart (previousVariantId) or a fixed variantType is configured. Otherwise a
    // cents-scaled quantity would be applied to the whole-unit variant and balloon the donation.
    if (isCustom) {
      return this.chosenCauseTarget.dataset[this.customVariantType == "whole" ? "variantId" : "centsVariantId"];
    }
    // Preset (non-custom) resolution below.
    if (this.previousVariantId) {
      return this.variantIds.includes(this.previousVariantId.toString())
        ? this.chosenCauseTarget.dataset["variantId"]
        : this.chosenCauseTarget.dataset["centsVariantId"];
    }
    if (this.variantType) {
      return this.chosenCauseTarget.dataset[this.variantType == "whole" ? "variantId" : "centsVariantId"];
    }
    return this.chosenCauseTarget.dataset[this.roundUp ? "centsVariantId" : "variantId"];
  }

  calculateAmount(variantId, quantity) {
    let amount = 0;
    if (this.roundUp && this.centsVariantIds.includes(variantId) && this.productValue == quantity) {
      amount = this.productValue;
    } else {
      const isCentsVariant = this.centsVariantIds.includes(variantId);
      amount = isCentsVariant ? this.productValue : this.wholeProductValue;
      if (!(this.roundUp && !isCentsVariant && quantity == 1)) {
        amount = ((this.roundUp || this.fixedWholeAmt ? 1 : this.currencyRate) * (amount * parseFloat(quantity)));
      }
    }
    return amount;
  }

  /* ---------- parent → iframe messages ---------- */

  displayWidget(e) {
    let data = {
      shadowParent: this.element.dataset.shadowParent,
      widgetSource: this.widgetSource,
      showCustomerDonationsWidget: (this.element.dataset.result == "true"),
      styles: JSON.parse(this.element.dataset.styles)
    };
    parent.postMessage(JSON.stringify(data), this.element.dataset.location);
    window.addEventListener("message", this.messageHandler.bind(this));
  }

  messageHandler(e) {
    try {
      let data = JSON.parse(e.data);
      if (data.requestHeight) this.handleRequestHeight();
      if (data.reloadChosenCause) this._syncQuantity(data.quantity);
      if (data.chosenCauseRemoved) this._resetWidget();
    } catch (e) {}
  }

  _syncQuantity(quantity) {
    this.selectedQuantity = quantity;
    this.causeIsSelected = true;
    if (this.hasCheckboxTarget) this.checkboxTarget.checked = true;
    const match = this.amountOptionTargets.find((o) => parseFloat(o.dataset["quantity"]) == parseFloat(quantity));
    if (match) {
      this.customMode = false;
      this.amountLabelTarget.textContent = (match.querySelector("span")?.textContent || match.textContent).trim();
      this._markSelected(this.amountOptionTargets, match);
      if (this.hasCustomOptionTarget) this.customOptionTarget.classList.remove("vcd-selected");
    } else {
      this.customMode = true;
      const amount = this.calculateAmount(this.previousVariantId, quantity);
      if (amount && isFinite(amount)) this.amountLabelTarget.textContent = this._formatCurrency(amount);
      this._markSelected(this.amountOptionTargets, null);
      if (this.hasCustomOptionTarget) this.customOptionTarget.classList.add("vcd-selected");
    }
    this.handleRequestHeight();
  }

  _resetWidget() {
    this.causeIsSelected = false;
    this.previousVariantId = null;
    this.customMode = false;
    // Return to the default preset (NOT a null quantity) so the next checkbox toggle can add
    // the donation immediately — leaving selectedQuantity null makes toggleEnabled bail and
    // forces the shopper to toggle twice to recover after an external removal.
    const preset = this.amountOptionTargets[0];
    this.selectedQuantity = preset ? preset.dataset["quantity"] : null;
    this._markSelected(this.amountOptionTargets, preset || null);
    if (this.hasCustomOptionTarget) this.customOptionTarget.classList.remove("vcd-selected");
    if (preset) this.amountLabelTarget.textContent = (preset.querySelector("span")?.textContent || preset.textContent).trim();
    if (this.hasCheckboxTarget) this.checkboxTarget.checked = false;
    this.causeSelectionTarget.dataset["chosenCause"] = "false";
    this.handleRequestHeight();
  }

  /* ---------- helpers ---------- */

  _markSelected(targets, selectedEl) {
    targets.forEach((el) => {
      const on = el === selectedEl;
      el.classList.toggle("vcd-selected", on);
      el.setAttribute("aria-selected", on.toString());
    });
  }

  _closeDropdowns() {
    if (this.hasAmountMenuTarget) {
      this.amountMenuTarget.classList.add("d-none");
      this.amountButtonTarget.setAttribute("aria-expanded", "false");
    }
    if (this.hasCauseMenuTarget) {
      this.causeMenuTarget.classList.add("d-none");
      this.chosenCauseTarget.setAttribute("aria-expanded", "false");
    }
    this._resetCustomInput();
    this.sendCurrentHeight();
  }

  _resetCustomInput() {
    if (this.hasCustomDisplayTarget) this.customDisplayTarget.classList.add("d-none");
    if (this.hasCustomOptionTarget) this.customOptionTarget.classList.remove("d-none");
  }

  _formatCurrency(amount) {
    return new Intl.NumberFormat(this.element.dataset.locale || "en", {
      style: "currency",
      currency: this.element.dataset.currency || "USD",
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  // Show the PayPal Giving Fund footer only for a PayPal-partner cause (mirrors the theme
  // web component, which gates the disclosure on the CURRENT cause's partner type). The
  // markup is always rendered when any cause is PayPal; we toggle its visibility here.
  _updatePpgfVisibility(isPaypal) {
    if (!this.hasFooterToggleTarget) return;
    this.footerToggleTarget.classList.toggle("d-none", !isPaypal);
    if (!isPaypal) {
      this.footerToggleTarget.setAttribute("aria-expanded", "false");
      if (this.hasFooterFullTarget) this.footerFullTarget.classList.add("d-none");
    }
    this.sendCurrentHeight();
  }

  saveCauseMetaData(metadata) {
    if (metadata) this.causeMetaData = JSON.parse(metadata);
  }

  handleRequestHeight() {
    const loc = this.element.dataset.location;
    const shadowParent = this.element.dataset.shadowParent;
    for (let step = 0; step < 15; step++) {
      setTimeout(function () {
        let scrollH = document.querySelector("body").scrollHeight;
        parent.postMessage(JSON.stringify({ shadowParent: shadowParent, customerDonationsHeight: scrollH }), loc);
      }, step > 0 ? (step * 100) : 100);
    }
  }

  sendCurrentHeight() {
    const currentHeight = document.querySelector("body").scrollHeight;
    parent.postMessage(JSON.stringify({
      shadowParent: this.element.dataset.shadowParent,
      customerDonationsHeight: currentHeight
    }), this.element.dataset.location);
  }

  get trackController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "track");
  }
}
